import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useCall } from "../components/layout/ActiveCallOverlay";
import { formatPhoneNumber } from "../lib/utils";
import { aiOperatorAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Languages, PhoneCall, Copy, Check, ChevronRight, X, RefreshCw, Clock, Lock } from "lucide-react";

const COLUMNS = [
  { key: "new", label: "New", color: "#2563eb", bg: "bg-blue-50" },
  { key: "in_progress", label: "In Progress", color: "#d97706", bg: "bg-amber-50" },
  { key: "done", label: "Done", color: "#059669", bg: "bg-emerald-50" },
];

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const whenText = (r) => [r.date, r.time].filter(Boolean).join(" ") || "ASAP / TBD";

const detailsForClipboard = (r) => [
  `Interpreter request (via phone / Avvy)`,
  `Caller: ${r.caller_name || "—"}  ${formatPhoneNumber(r.caller_phone) || ""}`,
  `Organization: ${r.organization || "—"}`,
  `Service: ${(r.service_type || "—").replace(/_/g, " ")}`,
  `Language: ${r.language_needed || "—"}`,
  `Modality: ${r.modality || "—"}`,
  `When: ${whenText(r)}`,
  `Location: ${r.location || "—"}`,
  `Priority: ${r.priority || "normal"}`,
  r.coordinator_note ? `Note: ${r.coordinator_note}` : "",
  r.crm_reference ? `CRM ref: ${r.crm_reference}` : "",
].filter(Boolean).join("\n");

function RequestCard({ r, onCall, onMove, onCancel, onSaveMeta }) {
  const [editing, setEditing] = useState(false);
  const [crm, setCrm] = useState(r.crm_reference || "");
  const [note, setNote] = useState(r.coordinator_note || "");

  const copy = async () => {
    try { await navigator.clipboard.writeText(detailsForClipboard(r)); toast.success("Details copied — paste into your CRM"); }
    catch { toast.error("Couldn't copy"); }
  };

  const nextKey = r.status === "new" ? "in_progress" : r.status === "in_progress" ? "done" : null;
  const nextLabel = r.status === "new" ? "Start" : r.status === "in_progress" ? "Mark done" : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`ir-card-${r.id}`}>
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-violet-50 grid place-items-center shrink-0">
          <Languages className="w-4 h-4 text-violet-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm truncate">{r.caller_name || "Caller"}</span>
            <span className="text-[11px] text-slate-400 font-mono">{formatPhoneNumber(r.caller_phone)}</span>
            {r.priority === "urgent" && <Badge className="text-[10px] py-0 bg-red-600">Urgent</Badge>}
            <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Clock className="w-3 h-3" />{timeAgo(r.created_at)}</span>
          </div>
          <p className="text-xs text-slate-700 mt-1 font-medium">
            {(r.service_type || "service").replace(/_/g, " ")} · {r.language_needed || "language TBD"} · {r.modality || "modality TBD"}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">When: {whenText(r)}{r.location ? ` · ${r.location}` : ""}</p>
          {r.organization && <p className="text-[11px] text-violet-700 mt-0.5">Org: {r.organization}</p>}
          {r.crm_reference && <p className="text-[11px] text-emerald-700 mt-0.5">CRM ref: {r.crm_reference}</p>}
          {r.coordinator_note && <p className="text-[11px] text-slate-500 mt-0.5 italic">“{r.coordinator_note}”</p>}
        </div>
      </div>

      {editing && (
        <div className="mt-2.5 space-y-2 border-t border-slate-100 pt-2.5">
          <Input value={crm} onChange={(e) => setCrm(e.target.value)} placeholder="CRM / order reference (from your other app)" className="h-8 text-xs" data-testid={`ir-crm-${r.id}`} />
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Coordinator note" rows={2} className="text-xs" data-testid={`ir-note-${r.id}`} />
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-800" onClick={() => { onSaveMeta(r, { crm_reference: crm, coordinator_note: note }); setEditing(false); }} data-testid={`ir-save-${r.id}`}>Save</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => onCall(r)} data-testid={`ir-call-${r.id}`}>
          <PhoneCall className="w-3 h-3" /> Call back
        </Button>
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={copy} data-testid={`ir-copy-${r.id}`}>
          <Copy className="w-3 h-3" /> Copy
        </Button>
        {!editing && (
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => setEditing(true)} data-testid={`ir-edit-${r.id}`}>
            CRM ref / note
          </Button>
        )}
        {nextKey && (
          <Button size="sm" className="h-7 gap-1 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => onMove(r, nextKey)} data-testid={`ir-advance-${r.id}`}>
            {r.status === "in_progress" ? <Check className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} {nextLabel}
          </Button>
        )}
        {r.status !== "cancelled" && r.status !== "done" && (
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-slate-400 hover:text-red-600" onClick={() => onCancel(r)} data-testid={`ir-cancel-${r.id}`}>
            <X className="w-3 h-3" /> Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

export default function InterpreterRequests() {
  const call = useCall();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user?.bylyngo_access) { setLoading(false); return; }
    try {
      const res = await aiOperatorAPI.getInterpreterRequests();
      setItems(res.data?.items || []);
      setCounts(res.data?.counts || {});
    } catch {
      toast.error("Couldn't load interpreter requests");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
    if (!user?.bylyngo_access) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, user]);

  if (user && !user.bylyngo_access) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center" data-testid="interpreter-requests-denied">
        <div className="mt-20 flex flex-col items-center gap-3 text-slate-500">
          <Lock className="w-10 h-10 text-slate-300" />
          <h1 className="text-lg font-semibold text-slate-700">Not available</h1>
          <p className="text-sm">You don't have access to this area.</p>
        </div>
      </div>
    );
  }

  const patch = async (r, data) => {
    try {
      const res = await aiOperatorAPI.updateInterpreterRequest(r.id, data);
      setItems((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...res.data } : x)));
      setCounts((c) => ({ ...c })); // refreshed on next poll
      load();
    } catch {
      toast.error("Couldn't update");
    }
  };

  const onCall = (r) => {
    if (!r.caller_phone) { toast.error("No phone number on file"); return; }
    if (!call?.makeCall) { toast.error("Phone not ready"); return; }
    call.makeCall(r.caller_phone, r.company_phone);
  };
  const onMove = (r, status) => patch(r, { status });
  const onCancel = (r) => patch(r, { status: "cancelled" });
  const onSaveMeta = (r, data) => { patch(r, data); toast.success("Saved"); };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="interpreter-requests-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Languages className="w-5 h-5 text-violet-600" /> Interpreter Requests
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Requests Avvy captured on the phone. Confirm, copy the details into your CRM, and mark done.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={load} data-testid="ir-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-20 text-center">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {COLUMNS.map((col) => {
            const colItems = items.filter((i) => (i.status || "new") === col.key);
            return (
              <Card key={col.key} className="border-slate-200" data-testid={`ir-column-${col.key}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold" style={{ color: col.color }}>{col.label}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${col.bg}`} style={{ color: col.color }}>
                      {counts[col.key] ?? colItems.length}
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    {colItems.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-6">Nothing here</p>
                    ) : (
                      colItems.map((r) => (
                        <RequestCard key={r.id} r={r} onCall={onCall} onMove={onMove} onCancel={onCancel} onSaveMeta={onSaveMeta} />
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
